import * as React from "react";
import {darken, type Theme, type Components} from "../..";
import Zoom from "../../../components/Zoom";
import {styled} from "../..";

/**
 * @typedef {undefined} RadioColor
 */
declare module "@mui/material/Radio" {
    interface RadioProps {
        /**
         * @param {RadioColor} [color] - The Next theme has no color variants for Radio.
         * Don't use this prop if you want the Next theme.
         */
        color?:
            | "primary"
            | "secondary"
            | "error"
            | "info"
            | "success"
            | "warning"
            | "default";
    }
}

type RadioOverrides = Components<Theme>["MuiRadio"];

const RadioBlankIcon = styled("span")(({theme}) => ({
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "&::before": {
        content: "''",
        position: "absolute",
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: `1.5px solid ${theme.color.border.subtle}`,

        ".Mui-disabled &": {
            borderColor: theme.color.border.disabled,
            backgroundColor: theme.color.fill.neutral.subtle,
        },
    },
}));

const RadioCheckedIcon = styled("span")(({theme}) => ({
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "&::before": {
        content: "''",
        position: "absolute",
        width: 16,
        height: 16,
        borderRadius: "50%",
        backgroundColor: theme.color.border.interactive,
    },

    "&::after": {
        content: "''",
        position: "absolute",
        width: 5,
        height: 5,
        borderRadius: "50%",
        backgroundColor: theme.color.icon.inverted.default,
    },

    ".Mui-disabled &": {
        "&::before": {
            backgroundColor: theme.color.icon.inverted.default,
            border: `1.5px solid ${theme.color.border.subtle}`,
        },
        "&::after": {
            backgroundColor: theme.color.icon.disabled,
        },
    },
}));

function DefaultIcon({checked = false}: {checked?: boolean}) {
    return (
        <>
            <RadioBlankIcon className="MuiRadio-blankIcon" />
            <Zoom in={checked} style={{transitionDuration: "150ms"}}>
                <RadioCheckedIcon
                    className="MuiRadio-checkedIcon"
                    sx={{position: "absolute"}}
                />
            </Zoom>
        </>
    );
}

const overrides: RadioOverrides = {
    defaultProps: {
        checkedIcon: <DefaultIcon checked />,
        icon: <DefaultIcon />,
        color: "default",
    },
    styleOverrides: {
        root: ({theme}) => ({
            "&:active, .MuiFormControlLabel-root:active &": {
                "& .MuiRadio-blankIcon::before": {
                    backgroundColor: theme.color.interaction.pressed,
                },
            },
            "&:hover, .MuiFormControlLabel-root:hover &": {
                backgroundColor: "unset",

                "& .MuiRadio-blankIcon::before": {
                    borderColor: darken(theme.color.border.subtle, 0.3),
                },
            },
            "&.Mui-focusVisible": {
                "& .MuiRadio-blankIcon::before, & .MuiRadio-checkedIcon::before": {
                    outline: `1.5px solid ${theme.color.border.focused}`,
                    outlineOffset: 2,
                },
            },
        }),
    },
};

export default overrides;
